import type { ReactElement } from 'react';
import { useArrangementTakes } from '@/lib/queries/takes';
import { formatMmSsFromMs } from '@/lib/format-time';
import { Card } from '@/components/atoms/card';
import { Micro } from '@/components/atoms/micro';
import type { Take } from '@/lib/types';

const MAX_RECENT_TAKES = 3;

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function TakeRow({ take }: { take: Take }): ReactElement {
  return (
    <li className="flex items-center justify-between py-2 text-sm">
      <div className="flex flex-col gap-0.5 text-[var(--ink)]">
        <span>{formatDateShort(take.recordedAt)}</span>
        <span className="text-xs text-[var(--ink-3)]">{take.label.prettyName}</span>
      </div>
      <span className="font-mono text-xs text-[var(--ink-3)] tabular-nums">
        {formatMmSsFromMs(take.durationSeconds * 1000)}
      </span>
    </li>
  );
}

export function TakesStrip({ arrangementId }: { arrangementId: string }): ReactElement {
  const { data } = useArrangementTakes(arrangementId);
  const takes = data?.content.slice(0, MAX_RECENT_TAKES) ?? [];

  return (
    <section aria-label="Recent takes">
      <Card padding="lg">
        <Micro className="mb-4">Recent takes</Micro>
        {takes.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--ink-3)]">No takes yet — tap Record to capture one.</p>
        ) : (
          <ul className="mt-3 divide-y divide-[var(--rule)]">
            {takes.map((take) => (
              <TakeRow key={take.takeId} take={take} />
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}
